/**
 * useListingPreferences — versioned localStorage preference reader/writer.
 *
 * Contract rules enforced here (from the frozen contract):
 *  - Reads the existing `{ data: ... }` envelope used by legacy Kendo helpers.
 *  - Accepts a caller-injected `legacyMigrator` to convert old shapes without
 *    this module knowing inspection-specific field names.
 *  - Search is NEVER read from or written to preferences.
 *  - After a successful legacy migration, the canonical versioned shape is
 *    written back to the same key.
 *  - Invalid JSON is removed from storage.
 *  - QuotaExceededError and other storage failures are swallowed with a warning.
 *  - Returns `null` when no usable preferences exist so callers can apply
 *    page-specific defaults.
 *
 * This module contains NO inspection-specific field names.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { sanitizeColumns } from './columnPreferences';
// ---------------------------------------------------------------------------
// Low-level storage helpers
// ---------------------------------------------------------------------------
function readRaw(cacheKey) {
    try {
        const item = localStorage.getItem(cacheKey);
        if (!item)
            return null;
        const parsed = JSON.parse(item);
        // Accept both `{ data: ... }` envelopes and bare preference objects.
        if (parsed && typeof parsed === 'object' && 'data' in parsed) {
            return parsed.data;
        }
        return parsed;
    }
    catch (_a) {
        try {
            localStorage.removeItem(cacheKey);
        }
        catch (_b) {
            // storage may be inaccessible; ignore secondary errors
        }
        return null;
    }
}
function removeStored(cacheKey) {
    try {
        localStorage.removeItem(cacheKey);
    }
    catch (_a) {
        // storage may be inaccessible; ignore secondary errors
    }
}
function writeCanonical(cacheKey, prefs) {
    const envelope = { data: prefs };
    try {
        localStorage.setItem(cacheKey, JSON.stringify(envelope));
    }
    catch (err) {
        if (err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22)) {
            console.warn(`[listings] Failed to save preferences because storage is full: ${cacheKey}`);
        }
        else {
            console.warn(`[listings] Failed to save preferences: ${cacheKey}`, err);
        }
    }
}
// ---------------------------------------------------------------------------
// Canonical version guard
// ---------------------------------------------------------------------------
function isCanonical(raw) {
    if (raw === null || typeof raw !== 'object')
        return false;
    const record = raw;
    if (record.version !== 1)
        return false;
    if (record.pageSize !== undefined) {
        const pageSize = record.pageSize;
        if (typeof pageSize !== 'number' || !Number.isInteger(pageSize) || pageSize <= 0)
            return false;
    }
    if (!isSort(record.sort))
        return false;
    if (!isPlainObject(record.filters))
        return false;
    if (!isStringArray(record.activeFilterKeys))
        return false;
    return isStringArray(record.visibleColumnKeys);
}
function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function isStringArray(value) {
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
}
function isSort(value) {
    if (value === null || value === undefined)
        return true;
    if (!isPlainObject(value))
        return false;
    return typeof value.field === 'string' && (value.direction === 'asc' || value.direction === 'desc');
}
function withoutSearch(filters) {
    if (!isPlainObject(filters))
        return filters;
    const rest = Object.assign({}, filters);
    delete rest.search;
    return rest;
}
// ---------------------------------------------------------------------------
// Load and normalise
// ---------------------------------------------------------------------------
function loadPreferences(cacheKey, policy, defaultFilters, defaultPageSize, legacyMigrator) {
    var _a, _b, _c, _d, _e, _f, _g;
    const raw = readRaw(cacheKey);
    if (!raw)
        return null;
    if (isCanonical(raw)) {
        // Already canonical — sanitize columns and return.
        const sanitized = {
            version: 1,
            pageSize: (_a = raw.pageSize) !== null && _a !== void 0 ? _a : defaultPageSize,
            sort: (_b = raw.sort) !== null && _b !== void 0 ? _b : null,
            filters: withoutSearch(Object.assign(Object.assign({}, defaultFilters), raw.filters)),
            activeFilterKeys: raw.activeFilterKeys,
            visibleColumnKeys: sanitizeColumns(raw.visibleColumnKeys, policy)
        };
        return sanitized;
    }
    if (isPlainObject(raw) && raw.version === 1) {
        removeStored(cacheKey);
        return null;
    }
    // Attempt legacy migration if a migrator was provided.
    if (!legacyMigrator)
        return null;
    const migrated = legacyMigrator(raw);
    if (!migrated)
        return null;
    const partial = migrated;
    const canonical = {
        version: 1,
        pageSize: (_c = partial.pageSize) !== null && _c !== void 0 ? _c : defaultPageSize,
        sort: (_d = partial.sort) !== null && _d !== void 0 ? _d : null,
        filters: withoutSearch(Object.assign(Object.assign({}, defaultFilters), ((_e = partial.filters) !== null && _e !== void 0 ? _e : {}))),
        activeFilterKeys: (_f = partial.activeFilterKeys) !== null && _f !== void 0 ? _f : [],
        visibleColumnKeys: sanitizeColumns((_g = partial.visibleColumnKeys) !== null && _g !== void 0 ? _g : [], policy)
    };
    // Write back canonical shape so next load is fast.
    writeCanonical(cacheKey, canonical);
    return canonical;
}
export function useListingPreferences(options) {
    const { cacheKey, policy, defaultFilters, defaultPageSize, legacyMigrator } = options;
    const [preferences, setPreferences] = useState(() => loadPreferences(cacheKey, policy, defaultFilters, defaultPageSize, legacyMigrator));
    // Re-load when the cache key changes (e.g. scope switch).
    // Intentionally depend only on cacheKey — other options change rarely and
    // we only want to re-load stored preferences when the target key changes.
    useEffect(() => {
        setPreferences(loadPreferences(cacheKey, policy, defaultFilters, defaultPageSize, legacyMigrator));
    }, [cacheKey]); // eslint-disable-line
    // Prefer a ref for `policy` so savePreferences stays referentially stable.
    // Callers often pass `columnPolicyForScope(...)` inline (new object each render).
    // If `policy` were a useCallback dep, savePreferences would change every render →
    // usePersistedListingPreferences would re-fire → setPreferences → infinite loop
    // (Series hang / IP-15282; same latent risk on Inspections).
    // Sync the ref in an effect (not during render) so an abandoned concurrent render
    // cannot publish an uncommitted policy into savePreferences.
    const policyRef = useRef(policy);
    useEffect(() => {
        policyRef.current = policy;
    }, [policy]);
    const savePreferences = useCallback((partial) => {
        const canonical = {
            version: 1,
            pageSize: partial.pageSize,
            sort: partial.sort,
            filters: withoutSearch(partial.filters),
            activeFilterKeys: partial.activeFilterKeys,
            visibleColumnKeys: sanitizeColumns(partial.visibleColumnKeys, policyRef.current)
        };
        setPreferences(canonical);
        writeCanonical(cacheKey, canonical);
    }, [cacheKey]);
    const clearPreferences = useCallback(() => {
        setPreferences(null);
        try {
            localStorage.removeItem(cacheKey);
        }
        catch (_a) {
            // ignore; storage may be inaccessible
        }
    }, [cacheKey]);
    return { preferences, savePreferences, clearPreferences };
}
